import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Wrench, CheckCircle2, ArrowRight, Phone, Mail,
  ShieldCheck, Clock, Zap, AlertTriangle, MapPin, ChevronRight
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Contact from '../components/Contact';
import Footer from '../components/Footer';
import './Servicepage.css';

import imgEntret from '../assets/entretientService.jpg';

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Entretien et Dépannage Climatisation Île-de-France",
  "provider": {
    "@type": "LocalBusiness",
    "name": "SOCLIMATE",
    "telephone": "+33618011895",
    "email": "soclimate.95@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "1 rue de Belloy",
      "addressLocality": "Villaines-sous-Bois",
      "postalCode": "95570",
      "addressCountry": "FR"
    },
    "areaServed": "Île-de-France",
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceUrl": "https://www.soclimate.fr/entretien-climatisation",
      "availableLanguage": "French"
    }
  },
  "description": "Entretien annuel, dépannage urgent et maintenance de climatisation en Île-de-France. Intervention rapide 24h. Toutes marques.",
  "serviceType": "Entretien et Dépannage Climatisation"
};

const PRESTATIONS = [
  { title: 'Entretien annuel', desc: 'Nettoyage complet, vérification des performances, rapport d\'intervention fourni.' },
  { title: 'Dépannage urgent', desc: 'Intervention rapide en cas de panne — disponible 6j/7 en Île-de-France.' },
  { title: 'Nettoyage des filtres', desc: 'Nettoyage et remplacement des filtres pour une qualité d\'air optimale.' },
  { title: 'Contrat de maintenance', desc: 'Contrat annuel pour entreprises et copropriétés avec interventions préventives.' },
  { title: 'Vérification performances', desc: 'Contrôle du COP, des pressions, températures et détection des fuites de fluide.' },
  { title: 'Recharge fluide frigorigène', desc: 'Détection et réparation des fuites, recharge en R32 ou R410A selon l\'équipement.' },
];

const AVANTAGES = [
  { icon: <Clock size={20} strokeWidth={1.8} />, title: 'Intervention en 24h', desc: 'Dépannage rapide — nous intervenons le jour même ou le lendemain sur rdv.' },
  { icon: <ShieldCheck size={20} strokeWidth={1.8} />, title: 'Toutes marques', desc: 'Daikin, Mitsubishi, Toshiba, Samsung, Panasonic, Atlantic et toutes autres marques.' },
  { icon: <Zap size={20} strokeWidth={1.8} />, title: 'Entretien obligatoire', desc: 'La réglementation impose un entretien annuel pour les systèmes > 4 kW (décret 2016).' },
  { icon: <AlertTriangle size={20} strokeWidth={1.8} />, title: 'Prévention des pannes', desc: 'Un entretien régulier évite 80% des pannes et prolonge la durée de vie de 5 ans.' },
];

const FAQ = [
  { q: 'L\'entretien de la climatisation est-il obligatoire ?', a: 'Oui. Le décret du 7 décembre 2016 impose un entretien annuel pour tout système de climatisation de puissance supérieure à 4 kW. À défaut, le propriétaire ou le gestionnaire engage sa responsabilité.' },
  { q: 'Quel est le prix d\'un entretien de climatisation ?', a: 'Un entretien annuel coûte entre 80 € et 180 € selon le type d\'équipement et le nombre d\'unités intérieures. Contactez-nous pour un devis précis.' },
  { q: 'Ma climatisation ne refroidit plus — que faire ?', a: 'Cela peut indiquer un filtre encrassé, une fuite de fluide frigorigène ou une panne du compresseur. Contactez-nous immédiatement au 06 18 01 18 95 — nous intervenons rapidement.' },
  { q: 'Intervenez-vous sur toutes les marques de climatisation ?', a: 'Oui, nos techniciens sont formés sur toutes les grandes marques : Daikin, Mitsubishi Electric, Toshiba, Samsung, Panasonic, Atlantic, Hitachi, LG et autres.' },
];

export default function EntretienClimatisation() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <Helmet>
        <title>Entretien et Dépannage Climatisation Île-de-France | SOCLIMATE</title>
        <meta name="description" content="Entretien annuel et dépannage climatisation en Île-de-France. Intervention rapide 24h. Toutes marques. Contrats de maintenance pour entreprises. Devis gratuit." />
        <meta name="keywords" content="entretien climatisation ile de france, dépannage climatisation, dépannage clim urgence, maintenance climatisation, entretien clim 95, réparation climatisation" />
        <link rel="canonical" href="https://www.soclimate.fr/entretien-climatisation" />
        <meta property="og:title" content="Entretien et Dépannage Climatisation Île-de-France | SOCLIMATE" />
        <meta property="og:description" content="Entretien annuel et dépannage climatisation. Intervention 24h. Toutes marques. Devis gratuit." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(SCHEMA)}</script>
      </Helmet>

      <Navbar />

      <main className="sp-page">

        <section className="sp-hero sp-hero--navy">
          <div className="sp-hero__bg" style={{ backgroundImage: `url(${imgEntret})` }} />
          <div className="sp-hero__veil" />
          <div className="container sp-hero__inner">
            <div className="sp-hero__breadcrumb">
              <Link to="/">Accueil</Link><ChevronRight size={14} />
              <Link to="/solution">Services</Link><ChevronRight size={14} />
              <span>Entretien & Dépannage</span>
            </div>
            <div className="sp-hero__badge sp-hero__badge--urgent">
              <AlertTriangle size={16} strokeWidth={2} />
              Dépannage Urgent · Intervention 24h
            </div>
            <h1 className="sp-hero__h1">
              Entretien & Dépannage<br />
              <span>Climatisation Île-de-France</span>
            </h1>
            <p className="sp-hero__sub">
              Panne, baisse de performance ou entretien réglementaire : nos techniciens
              interviennent rapidement sur tout l'Île-de-France. Toutes marques,
              particuliers et professionnels.
            </p>
            <div className="sp-hero__ctas">
              <a href="tel:0618011895" className="sp-cta-primary sp-cta-primary--urgent">
                <Phone size={16} strokeWidth={2} /> Urgence — 06 18 01 18 95
              </a>
              <a href="#contact" className="sp-cta-ghost">
                Devis entretien <ArrowRight size={15} strokeWidth={2} />
              </a>
            </div>
            <div className="sp-hero__stats">
              <div className="sp-stat"><strong>24h</strong><span>Intervention</span></div>
              <div className="sp-stat__sep" />
              <div className="sp-stat"><strong>Toutes</strong><span>Marques</span></div>
              <div className="sp-stat__sep" />
              <div className="sp-stat"><strong>10+</strong><span>Ans d'expérience</span></div>
              <div className="sp-stat__sep" />
              <div className="sp-stat"><strong>500+</strong><span>Entretiens/an</span></div>
            </div>
          </div>
        </section>

        {/* URGENCE BAND */}
        <div className="sp-urgence-band">
          <div className="container sp-urgence-band__inner">
            <AlertTriangle size={20} strokeWidth={2} />
            <span><strong>Panne de climatisation ?</strong> Appelez-nous maintenant — intervention rapide en Île-de-France.</span>
            <a href="tel:0618011895" className="sp-urgence-band__btn">06 18 01 18 95</a>
          </div>
        </div>

        <section className="sp-intro">
          <div className="container sp-intro__grid sp-intro__grid--single">
            <div className="sp-intro__text">
              <h2>Entretien et dépannage climatisation en Île-de-France</h2>
              <p>
                L'entretien régulier de votre climatisation est <strong>obligatoire
                par décret</strong> pour les systèmes supérieurs à 4 kW.
                SOCLIMATE assure l'entretien annuel, la maintenance préventive
                et le dépannage urgent dans le <strong>Val-d'Oise (95)</strong>
                et toute l'Île-de-France.
              </p>
              <p>
                Nos techniciens interviennent sur <strong>toutes les marques</strong> :
                Daikin, Mitsubishi Electric, Toshiba, Samsung, Panasonic, Atlantic,
                Hitachi, LG et bien d'autres. Particuliers, entreprises, copropriétés.
              </p>
              <ul className="sp-check-list">
                <li><CheckCircle2 size={16} strokeWidth={2} />Intervention le jour même possible</li>
                <li><CheckCircle2 size={16} strokeWidth={2} />Rapport d'intervention fourni</li>
                <li><CheckCircle2 size={16} strokeWidth={2} />Contrats de maintenance entreprises</li>
                <li><CheckCircle2 size={16} strokeWidth={2} />Conformité réglementaire garantie</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="sp-section sp-section--gray">
          <div className="container">
            <div className="sp-head">
              <p className="sp-eyebrow"><span />NOS PRESTATIONS</p>
              <h2>Entretien & maintenance<br /><em>tous équipements</em></h2>
            </div>
            <div className="sp-grid-3">
              {PRESTATIONS.map((p, i) => (
                <div key={i} className="sp-presta-card">
                  <div className="sp-presta-card__num">0{i + 1}</div>
                  <h3>{p.title}</h3>
                  <p>{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="sp-section">
          <div className="container">
            <div className="sp-head">
              <p className="sp-eyebrow"><span />POURQUOI ENTRETENIR</p>
              <h2>Les raisons d'un <em>entretien régulier</em></h2>
            </div>
            <div className="sp-grid-2">
              {AVANTAGES.map((a, i) => (
                <div key={i} className="sp-avantage">
                  <div className="sp-avantage__icon">{a.icon}</div>
                  <div><h3>{a.title}</h3><p>{a.desc}</p></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="sp-section sp-section--gray">
          <div className="container">
            <div className="sp-head">
              <p className="sp-eyebrow"><span />ZONES D'INTERVENTION</p>
              <h2>Dépannage climatisation<br /><em>dans toute l'Île-de-France</em></h2>
            </div>
            <div className="sp-zones">
              {['Val-d\'Oise (95)', 'Paris (75)', 'Yvelines (78)', 'Essonne (91)', 'Hauts-de-Seine (92)', 'Seine-Saint-Denis (93)', 'Val-de-Marne (94)', 'Seine-et-Marne (77)'].map((z, i) => (
                <div key={i} className="sp-zone-pill"><MapPin size={13} strokeWidth={2} /> {z}</div>
              ))}
            </div>
          </div>
        </section>

        <section className="sp-section">
          <div className="container sp-faq-wrap">
            <div className="sp-head">
              <p className="sp-eyebrow"><span />FAQ</p>
              <h2>Questions fréquentes<br /><em>sur l'entretien climatisation</em></h2>
            </div>
            <div className="sp-faq">
              {FAQ.map((item, i) => (
                <details key={i} className="sp-faq__item">
                  <summary className="sp-faq__q">{item.q}</summary>
                  <p className="sp-faq__a">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        

      </main>
      <Contact />
      <Footer />
    </>
  );
}